/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Image Analysis Service - Smart threshold calculation using histogram analysis
 * Updated: 2026-01-11
 *
 * Smart Threshold Algorithm:
 * - Analyzes histogram of image luminance
 * - Finds percentile 75 (75% of pixels are darker than this luminance)
 * - Converts luminance to intensity threshold
 * - Auto-adjusts threshold for each unique image
 */

/**
 * Calculate smart detail threshold using histogram analysis
 *
 * Algorithm:
 * 1. Convert all pixels to luminance (brightness) values
 * 2. Create histogram of luminance distribution
 * 3. Remove white background (luminance > 220)
 * 4. Find percentile 33 - the luminance value where 33% of pixels are darker (aggressive detail)
 * 5. Convert luminance percentile to intensity threshold
 * 6. Return threshold value (0-255)
 *
 * Result (Percentile 33 - Aggressive Detail):
 * - Dark images (avg luminance < 100) → threshold ≈ 20-40
 * - Medium images (avg luminance 100-180) → threshold ≈ 35-60
 * - Light images (avg luminance > 180) → threshold ≈ 50-70
 */
export const calculateSmartThresholdWithHistogram = (
  imageBase64: string
): Promise<number> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          console.warn('⚠️ [SmartThreshold] Failed to get canvas context, using default 100');
          return resolve(100);
        }

        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Step 1: Create histogram (0-255 luminance values)
        const histogram = new Array(256).fill(0);
        let totalPixels = 0;
        let totalLuminance = 0;

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];

          // Skip transparent pixels
          if (a < 10) continue;

          // Calculate luminance using standard formula
          const luminance = Math.round(0.299 * r + 0.587 * g + 0.114 * b);

          // Skip white background (luminance > 220)
          if (luminance > 220) continue;

          histogram[luminance]++;
          totalPixels++;
          totalLuminance += luminance;
        }

        // Step 2: Calculate average luminance for debugging
        const avgLuminance = totalLuminance / totalPixels;

        // Step 3: Find percentile 33 (33% of pixels are darker) - More aggressive detail
        // Changed from median (50%) to 33% percentile for better light image preservation
        let cumulative = 0;
        let percentile33Luminance = 128;

        for (let i = 0; i < 256; i++) {
          cumulative += histogram[i];

          // Find the luminance value where we reach 33% of pixels
          if (cumulative >= totalPixels * 0.33) {
            percentile33Luminance = i;
            break;
          }
        }

        // Step 4: Convert luminance to intensity
        // intensity = 1 - (luminance / 255)
        const percentile33Intensity = 1 - (percentile33Luminance / 255);

        // Step 5: Convert intensity back to threshold value (0-255)
        const smartThreshold = Math.round(percentile33Intensity * 255);

        // Clamp to reasonable range (20-70 for very aggressive detail preservation)
        const finalThreshold = Math.max(20, Math.min(70, smartThreshold));

        console.log(`📊 [SmartThreshold] Histogram Analysis (Percentile 33 - Aggressive):`);
        console.log(`   Average luminance: ${avgLuminance.toFixed(1)}`);
        console.log(`   Percentile 33 luminance: ${percentile33Luminance}`);
        console.log(`   Percentile 33 intensity: ${percentile33Intensity.toFixed(3)}`);
        console.log(`   Calculated threshold: ${smartThreshold}`);
        console.log(`   Final threshold: ${finalThreshold} (clamped 20-70)`);

        resolve(finalThreshold);
      } catch (err) {
        console.error('❌ [SmartThreshold] Error calculating threshold:', err);
        reject(err);
      }
    };

    img.onerror = () => {
      console.warn('⚠️ [SmartThreshold] Failed to load image, using default 100');
      resolve(100);
    };

    img.src = imageBase64;
  });
};
